/* ── Panneau d'hypothèses ajustables de la projection actuarielle ──
 * Repliable, fermé par défaut. `overrides` ne contient que les clés que l'admin a
 * explicitement modifiées (fractions/unités API, pas des pourcentages) ; les champs
 * affichent `assumptions` (valeurs résolues côté serveur, défauts inclus) tant que
 * l'admin n'a rien touché.
 */
const CONFIDENCE_LEVELS = [
  { value: 1.0, label: "Faible" },
  { value: 1.64, label: "Standard" },
  { value: 2.33, label: "Élevée" },
];

const HORIZON_OPTIONS = [10, 20, 30, 50];

const fieldLabelCls = "block text-[11px] font-medium uppercase tracking-wide text-slate-400";
const inputCls =
  "w-full pl-3 pr-8 py-2 text-sm rounded-lg border border-slate-200 bg-white text-slate-900 " +
  "focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all";

const RateField = ({ label, valueFraction, defaultFraction, onChange, suffix = "%" }) => {
  const toPct = (f) => (f == null ? "" : Math.round(f * 1000) / 10);
  const pct = toPct(valueFraction);
  const defaultPct = toPct(defaultFraction);
  return (
    <div className="space-y-1">
      <label className={fieldLabelCls}>{label}</label>
      <div className="relative">
        <input
          type="number"
          value={pct}
          step="0.1"
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === "" ? undefined : parseFloat(raw) / 100);
          }}
          className={inputCls}
        />
        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>
      </div>
      {defaultPct !== "" && <p className="text-[10px] text-slate-300">défaut : {defaultPct} %</p>}
    </div>
  );
};

const NumberField = ({ label, value, defaultValue, onChange, suffix, step = 1, min }) => (
  <div className="space-y-1">
    <label className={fieldLabelCls}>{label}</label>
    <div className="relative">
      <input
        type="number"
        value={value ?? ""}
        step={step}
        min={min}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : parseFloat(raw));
        }}
        className={inputCls}
      />
      {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
    {defaultValue != null && <p className="text-[10px] text-slate-300">défaut : {defaultValue}{suffix ? ` ${suffix}` : ""}</p>}
  </div>
);

const SegmentedControl = ({ options, value, onChange, closeMatch }) => (
  <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
    {options.map((opt) => {
      const active = closeMatch ? closeMatch(opt.value, value) : opt.value === value;
      return (
        <button
          key={opt.label}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
            active ? "bg-emerald-600 text-white" : "bg-white text-slate-500 hover:bg-slate-50"
          }`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const ProjectionAssumptions = ({ open, onToggle, assumptions, defaults, overrides, onChange }) => {
  const set = (key) => (value) => {
    const next = { ...overrides };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  const reset = () => onChange({});

  const a = assumptions || {};
  const d = defaults || {};

  const summaryLine = assumptions
    ? [
        `${a.member_growth_rate >= 0 ? "+" : ""}${Math.round(a.member_growth_rate * 1000) / 10} %/an`,
        `entrée à ${a.entry_age} ans`,
        `prudence ${CONFIDENCE_LEVELS.find((c) => c.value === a.confidence_k)?.label?.toLowerCase() || "standard"}`,
      ].join(" · ")
    : "…";

  return (
    <div className="mb-3 border border-slate-200/70 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50/60 hover:bg-slate-50 transition-colors text-left"
      >
        <span className="text-xs font-semibold text-slate-600">
          Hypothèses <span className="font-normal text-slate-400">· {summaryLine}</span>
        </span>
        <svg
          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="px-4 py-4 space-y-4 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <span className={fieldLabelCls}>Horizon</span>
              <SegmentedControl
                options={HORIZON_OPTIONS.map((y) => ({ value: y, label: `${y} ans` }))}
                value={a.horizon_years}
                onChange={set("horizon_years")}
              />
            </div>
            <div className="space-y-1">
              <span className={fieldLabelCls}>Prudence</span>
              <SegmentedControl
                options={CONFIDENCE_LEVELS}
                value={a.confidence_k}
                closeMatch={(opt, val) => Math.abs(opt - val) < 0.01}
                onChange={set("confidence_k")}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <RateField
              label="Croissance des membres"
              valueFraction={a.member_growth_rate}
              defaultFraction={d.member_growth_rate}
              onChange={set("member_growth_rate")}
            />
            <NumberField
              label="Âge d'entrée"
              value={a.entry_age}
              defaultValue={d.entry_age}
              suffix="ans"
              onChange={set("entry_age")}
            />
            <RateField
              label="Part d'hommes (entrants)"
              valueFraction={a.entry_male_share}
              defaultFraction={d.entry_male_share}
              onChange={set("entry_male_share")}
            />
            <NumberField
              label="Réserve"
              value={a.reserve_years}
              defaultValue={d.reserve_years}
              suffix="ans"
              step={1}
              min={1}
              onChange={set("reserve_years")}
            />
            <RateField
              label="Taux de collecte"
              valueFraction={a.collection_rate}
              defaultFraction={d.collection_rate}
              onChange={set("collection_rate")}
            />
            <RateField
              label="Croissance cotisation"
              valueFraction={a.cotisation_growth_rate}
              defaultFraction={d.cotisation_growth_rate}
              onChange={set("cotisation_growth_rate")}
            />
            <RateField
              label="Inflation du coût"
              valueFraction={a.cost_inflation_rate}
              defaultFraction={d.cost_inflation_rate}
              onChange={set("cost_inflation_rate")}
            />
            <RateField
              label="Rendement du fonds"
              valueFraction={a.investment_yield_rate}
              defaultFraction={d.investment_yield_rate}
              onChange={set("investment_yield_rate")}
            />
            <NumberField
              label="Cotisation (et si…)"
              value={a.cotisation_amount}
              defaultValue={d.cotisation_amount}
              suffix="€"
              step={5}
              onChange={set("cotisation_amount")}
            />
            <NumberField
              label="Coût rapatriement (et si…)"
              value={a.avg_repatriation_cost}
              defaultValue={d.avg_repatriation_cost}
              suffix="€"
              step={100}
              onChange={set("avg_repatriation_cost")}
            />
            <NumberField
              label="Solde de départ"
              value={a.initial_fund_balance}
              defaultValue={d.initial_fund_balance}
              suffix="€"
              step={100}
              onChange={set("initial_fund_balance")}
            />
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={reset}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
            >
              Réinitialiser les hypothèses
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectionAssumptions;
